import PageHeader from "../components/PageHeader";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Coins, Scale, GraduationCap, HeartHandshake, Building2, Globe2 } from "lucide-react";
import { useLang } from "../i18n/LanguageProvider";

const SERVICES_IMG =
  "https://images.unsplash.com/photo-1498262257252-c282316270bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1pbmltYWxpc3QlMjBjdXJ2ZXN8ZW58MHx8fHwxNzg0MDY1MDY1fDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: Coins,
    title: "Investment Stewardship",
    summary: "A single portfolio, run by a single committee, aligned with a single family.",
    detail: "We construct and manage a bespoke portfolio across public markets, private equity, private credit, real estate and hedge strategies. Managers are selected through institutional due diligence; terms are negotiated as a fiduciary; performance is reported quarterly with full transparency of holdings, fees and rationale."
  },
  {
    icon: Building2,
    title: "Family Governance",
    summary: "Constitutions, councils and forums that make wealth continuous.",
    detail: "We help principal families design and operate the human infrastructure of enduring capital: family constitutions, family councils, next-generation programmes, dispute-resolution protocols and codified decision rights across trustees, boards and operating companies."
  },
  {
    icon: Scale,
    title: "Structuring & Legacy",
    summary: "Trusts, holding vehicles and succession — engineered for a century, not a cycle.",
    detail: "In coordination with the family's chosen law firms and trustees, we architect and maintain the trust and holding structures that transfer wealth intact across borders and generations. Every structure is stress-tested against tax, regulatory and reputational risk."
  },
  {
    icon: HeartHandshake,
    title: "Philanthropy & Impact",
    summary: "Giving that is measured, strategic and generational.",
    detail: "We help families formalise their philanthropic mission — endowments, foundations, DAF vehicles, impact portfolios — and instrument them with the same rigour as the investment portfolio. Purpose, measured."
  },
  {
    icon: GraduationCap,
    title: "Next-Generation Education",
    summary: "Preparing heirs, not just estates.",
    detail: "Structured programmes for rising family members: financial literacy, board readiness, values workshops, mentored internships across our managers and operating relationships. Because the safest portfolio is a prepared successor."
  },
  {
    icon: Globe2,
    title: "Concierge & Coordination",
    summary: "One office, one point of contact, one privileged information flow.",
    detail: "Consolidated reporting, cash management, currency operations, private aviation and real-estate coordination, cyber-security advisory, and secretarial support for family boards. Everything, orchestrated quietly."
  }
];

export default function Services() {
  const { t } = useLang();
  return (
    <>
      <PageHeader
        eyebrow={t("page.services.eyebrow")}
        title={t("page.services.title")}
        lede={t("page.services.lede")}
      />

      <section className="relative pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={SERVICES_IMG}
                alt="Architectural abstract"
                className="w-full h-[520px] object-cover"
                style={{ filter: "brightness(0.5)" }}
              />
              <div className="absolute inset-0 border border-[var(--bw-border)]" />
            </div>
            <div className="mt-8 text-muted text-sm leading-relaxed max-w-md">
              Each engagement begins with a private mapping of the family's balance sheet,
              governance and intentions. Only after that mapping do we accept an appointment.
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {services.map((s, i) => (
                <div
                  key={s.title}
                  className={`card-lux ${i % 2 === 1 ? "sm:translate-y-6" : ""}`}
                  data-testid={`service-card-${i}`}
                >
                  <s.icon size={22} className="text-gold mb-5" strokeWidth={1.2} />
                  <h3 className="font-serif-display text-xl text-white mb-2">{s.title}</h3>
                  <p className="text-muted text-sm leading-relaxed">{s.summary}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--bw-border)] py-24 bg-surface">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-8">In Depth</div>
          <h2 className="font-serif-display text-3xl lg:text-4xl text-white mb-12 leading-tight">
            The disciplines, expanded.
          </h2>

          <Accordion type="single" collapsible className="w-full" data-testid="services-accordion">
            {services.map((s, i) => (
              <AccordionItem key={s.title} value={`item-${i}`} className="border-b border-[var(--bw-border)]">
                <AccordionTrigger className="text-left py-6 text-white hover:text-gold hover:no-underline">
                  <div className="flex items-center gap-4">
                    <span className="font-serif-display text-gold text-lg w-8">0{i + 1}</span>
                    <span className="font-serif-display text-xl">{s.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted text-base leading-relaxed pb-8 pl-12">
                  {s.detail}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
